import { WorkawareApi } from 'apis/WorkawareApi'
import { JourneyModel } from 'data/models/JourneyModel'
import { JourneyCheckPointModel } from 'data/models/JourneyCheckPointModel'
import { transform, TransformMethods } from 'data/transform'

export class journeyService {
  private _api: WorkawareApi
  private _journeyTransform: TransformMethods<JourneyModel>

  constructor(api: WorkawareApi) {
    this._api = api
    this._journeyTransform = transform<JourneyModel>(JourneyModel)
  }
  getJourney = (filter?: { dateFrom?: string; dateTo?: string }) => {
    return new Promise<JourneyModel[]>((resolve, reject) => {
      this._api.journeyManagement
        .getList(filter)
        .then((result) => resolve(this._journeyTransform.arrayFrom(result)))
        .catch((error) => reject(error))
    })
  }
  createJourney =(journey:JourneyModel) =>
  {
    return new Promise<JourneyModel>((resolve,reject) =>
    {
      this._api.journeyManagement
          .create(journey)
          .then((journey) =>resolve(this._journeyTransform.from(journey)))
          .catch((error) =>reject(error))
    })
  }
  updateJourney = (id: number, journey: JourneyModel) => {
    return new Promise((resolve, reject) => {
      this._api.journeyManagement
        .update(id, journey)
        .then((journey) => resolve(this._journeyTransform.from(journey)))
        .catch((error) => reject(error))
    })
  }
  deleteJourney = (id: number) => {
    return new Promise<void>((resolve, reject) => {
      this._api.journeyManagement
        .delete(id)
        .then(() => resolve())
        .catch((error) => reject(error))
    })
  }
 removeUserFromJourney = (journeyUserId: number) => {
  return new Promise<void>((resolve, reject) => {
    this._api.journeyUsers
      .delete(journeyUserId)
      .then(() => resolve())
      .catch((error) => reject(error))
  })
}


getJourneyById = (id: number) => {
  return new Promise<JourneyModel>((resolve, reject) => {
    this._api.journeyManagement
      .get(id)
      .then((result) => {
        const journey = this._journeyTransform.from(result)
        const rawCheckpoints = result.JourneyCheckpoints ?? result.journeyCheckpoints
        if (rawCheckpoints?.length) {
          journey.CheckPoints = rawCheckpoints.map((cp: any) => {
            const model = new JourneyCheckPointModel()
            model.Id = cp.id ?? cp.Id
            model.Name = cp.name ?? cp.Name
            model.JourneyId = cp.journeyId ?? cp.JourneyId
            model.CompanyId = cp.companyId ?? cp.CompanyId
            model.Description = cp.description ?? cp.Description
            model.DepartureLocation = cp.departureLocation ?? cp.DepartureLocation
            model.ArrivalLocation = cp.arrivalLocation ?? cp.ArrivalLocation
            model.DeparturePointLatitude = cp.departurePointLatitude ?? cp.DeparturePointLatitude
            model.DeparturePointLongitude = cp.departurePointLongitude ?? cp.DeparturePointLongitude
            model.ArrivalPointLatitude = cp.arrivalPointLatitude ?? cp.ArrivalPointLatitude
            model.ArrivalPointLongitude = cp.arrivalPointLongitude ?? cp.ArrivalPointLongitude
            model.EstimatedDepartureOn = cp.estimatedDepartureOn ?? cp.EstimatedDepartureOn
            model.EstimatedArrivalOn = cp.estimatedArrivalOn ?? cp.EstimatedArrivalOn
            model.TravelMethodId = cp.travelMethodId ?? cp.TravelMethodId
            model.StatusId = cp.statusId ?? cp.StatusId
            model.UserStatuses = cp.userStatuses ?? cp.UserStatuses
            return model
          })
        } else {
          journey.CheckPoints = []
        }

        // Explicitly carry over JourneyUsers from the raw response so the
        // drawer's user-population effect can read assigned/notification users.
        const rawUsers = result.JourneyUsers ?? result.journeyUsers
        if (rawUsers) {
          journey.JourneyUsers = rawUsers
        }

        resolve(journey)
      })
      .catch((error) => reject(error))
  })
}

sendJourneyNotification = (id: number, newUserIds?: number[]): Promise<{ EmailsSent?: number; EmailsFailed?: number; UsersWithNoEmail?: number; SentToAddresses?: string; FailedAddresses?: string; FailureReasons?: string }> =>
  new Promise((resolve, reject) => {
    this._api.journeyManagement
      .sendNotification(id, newUserIds)
      .then((result: any) => resolve({
        EmailsSent: result?.EmailsSent,
        EmailsFailed: result?.EmailsFailed,
        UsersWithNoEmail: result?.UsersWithNoEmail,
        SentToAddresses: result?.SentToAddresses,
        FailedAddresses: result?.FailedAddresses,
        FailureReasons: result?.FailureReasons,
      }))
      .catch((error) => reject(error))
  })

// Maps one raw OData journey (with expanded checkpoints/users) to the list model. Shared by the
// "load everything" and the paged loaders so both produce identical objects.
private toListModel = (result: any): JourneyModel => {
  const journey = this._journeyTransform.from(result)
  const rawCheckpoints = result.journeyCheckpoints ?? result.JourneyCheckpoints
  journey.CheckPoints = (rawCheckpoints ?? []).map((cp: any) => {
    const model = new JourneyCheckPointModel()
    model.Id = cp.id ?? cp.Id
    model.Name = cp.name ?? cp.Name
    model.JourneyId = cp.journeyId ?? cp.JourneyId
    model.DepartureLocation = cp.departureLocation ?? cp.DepartureLocation
    model.ArrivalLocation = cp.arrivalLocation ?? cp.ArrivalLocation
    model.DeparturePointLatitude = cp.departurePointLatitude ?? cp.DeparturePointLatitude
    model.DeparturePointLongitude = cp.departurePointLongitude ?? cp.DeparturePointLongitude
    model.ArrivalPointLatitude = cp.arrivalPointLatitude ?? cp.ArrivalPointLatitude
    model.ArrivalPointLongitude = cp.arrivalPointLongitude ?? cp.ArrivalPointLongitude
    model.EstimatedDepartureOn = cp.estimatedDepartureOn ?? cp.EstimatedDepartureOn
    model.EstimatedArrivalOn = cp.estimatedArrivalOn ?? cp.EstimatedArrivalOn
    model.TravelMethodId = cp.travelMethodId ?? cp.TravelMethodId
    model.StatusId = cp.statusId ?? cp.StatusId
    model.UserStatuses = cp.userStatuses ?? cp.UserStatuses
    return model
  })
  const rawUsers = result.JourneyUsers ?? result.journeyUsers
  if (rawUsers) {
    journey.JourneyUsers = rawUsers
  }
  return journey
}

getJourneysWithCheckpoints = (): Promise<JourneyModel[]> => {
  return this._api.journeyManagement.getAllWithCheckpoints().then((results: any[]) =>
    results.map((result: any) => this.toListModel(result))
  )
}

// One page of journeys (server-side paging). `top` undefined = no limit ("All").
getJourneysPage = (skip: number, top: number | undefined, query: string): Promise<{ items: JourneyModel[]; total: number }> => {
  return this._api.journeyManagement.getPaged(skip, top, query).then((res: any) => {
    const rows: any[] = res?.value ?? []
    return {
      items: rows.map((result: any) => this.toListModel(result)),
      total: res?.['@odata.count'] ?? rows.length,
    }
  })
}

// True when another journey (excluding `excludeId`) already has this name (case-insensitive, trimmed).
journeyNameExists = (name: string | undefined, excludeId?: number): Promise<boolean> => {
  const normalized = (name ?? '').trim().toLowerCase().replace(/'/g, "''")
  if (!normalized) return Promise.resolve(false)
  const clauses = [`tolower(trim(Name)) eq '${normalized}'`]
  if (excludeId) clauses.push(`Id ne ${excludeId}`)
  return this._api.journeyManagement
    .findByName(`$filter=${encodeURIComponent(clauses.join(' and '))}`)
    .then((res: any) => (res?.value?.length ?? 0) > 0)
}

}
export const journeyServices = new journeyService(new WorkawareApi());
